import { useTranslation } from "react-i18next";
import React, { useMemo } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Truck,
  Package,
  Calendar,
  MapPin,
  CheckCircle2,
  Clock,
  Box,
  ClipboardList,
  Phone,
  FileText,
  Hash,
} from "lucide-react";
import { formatDate } from "@/lib/date-utils";
import { formatCentsAsDollars } from "@/lib/price-utils";
import type { HajocaSalesOrderInquiryResponse } from "@shared/types/hajoca";
import {
  formatHajocaShipmentAddress,
  getHajocaShipmentAccountName,
  formatHajocaShipmentLineItemPrice,
  formatHajocaQuantity,
} from "@shared/hajoca-utils";

interface OrderFulfillmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  fulfillmentData: HajocaSalesOrderInquiryResponse | null;
  poNumber: string;
  hajocaOrderId?: string;
}

// Format unit of measure to be more readable (lowercase for price display)
const formatUOMShort = (uom: string | undefined): string => {
  if (!uom) return "ea";
  const uomMap: Record<string, string> = {
    ea: "ea",
    EA: "ea",
    ft: "ft",
    FT: "ft",
    in: "in",
    IN: "in",
    lb: "lb",
    LB: "lb",
    pc: "pc",
    PC: "pc",
    pk: "pk",
    PK: "pk",
    bx: "bx",
    BX: "bx",
    cs: "cs",
    CS: "cs",
    rl: "rl",
    RL: "rl",
  };
  return uomMap[uom] || uom.toLowerCase();
};

// Status step component for the progress timeline
const StatusStep = ({
  icon: Icon,
  label,
  isActive,
  isComplete,
}: {
  icon: React.ElementType;
  label: string;
  isActive: boolean;
  isComplete: boolean;
}) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
        isComplete
          ? "bg-green-500 text-white"
          : isActive
            ? "bg-primary text-white ring-2 ring-primary/20"
            : "bg-slate-200 text-slate-400"
      }`}
    >
      {isComplete ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
    </div>
    <span
      className={`text-xs font-medium ${
        isComplete || isActive ? "text-slate-700" : "text-slate-400"
      }`}
    >
      {label}
    </span>
  </div>
);

export const OrderFulfillmentModal: React.FC<OrderFulfillmentModalProps> = ({
  isOpen,
  onClose,
  fulfillmentData,
  poNumber,
  hajocaOrderId,
}) => {
  const { t } = useTranslation();
  if (!fulfillmentData?.data) {
    return null;
  }

  const order = fulfillmentData.data;
  const shipments = order.attributes?.shipments || [];
  const lineItems = order.attributes?.lineItems || [];

  // Calculate fulfillment progress
  const hasShipments = shipments.length > 0;

  // Create a lookup map from order line items by index (1-based orderlineitemId)
  // This allows us to show product details in shipment line items
  const lineItemsMap = useMemo(() => {
    const map = new Map<string, any>();
    lineItems.forEach((item: any, idx: number) => {
      // orderlineitemId appears to be 1-based index
      map.set(String(idx + 1), item);
    });
    return map;
  }, [lineItems]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto p-0">
        <div className="bg-white">
          {/* Compact Header */}
          <div className="border-b border-slate-200 bg-slate-50 px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 rounded-lg p-2.5">
                  <Truck className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">{poNumber}</h1>
                  {hajocaOrderId && (
                    <p className="text-slate-500 text-sm flex items-center gap-1.5 mt-0.5">
                      <Hash className="h-3.5 w-3.5" />
                      {t("components.orders.orderFulfillmentModal.hajocaOrder")} {hajocaOrderId}
                    </p>
                  )}
                </div>
              </div>
              <Badge className="bg-green-100 hover:bg-green-100 text-green-700 border border-green-200 text-sm px-3 py-1 font-medium">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                {t("components.orders.orderFulfillmentModal.submitted")}
              </Badge>
            </div>

            {/* Compact Progress Timeline */}
            <div className="mt-5 bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <StatusStep icon={ClipboardList} label={t("components.orders.orderFulfillmentModal.placed")} isActive={false} isComplete={true} />
                <div className="flex-1 h-0.5 mx-2 bg-green-400 rounded" />
                <StatusStep icon={Box} label={t("components.orders.orderFulfillmentModal.processing")} isActive={!hasShipments} isComplete={hasShipments} />
                <div className={`flex-1 h-0.5 mx-2 rounded ${hasShipments ? "bg-green-400" : "bg-slate-200"}`} />
                <StatusStep icon={Truck} label={t("components.orders.orderFulfillmentModal.shipping")} isActive={hasShipments} isComplete={false} />
                <div className="flex-1 h-0.5 mx-2 bg-slate-200 rounded" />
                <StatusStep icon={CheckCircle2} label={t("components.orders.orderFulfillmentModal.delivered")} isActive={false} isComplete={false} />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-6 space-y-6">
            {/* Order Items Section */}
            {lineItems.length > 0 && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-5 py-3">
                  <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <Package className="h-4 w-4 text-primary" />
                    {t("components.orders.orderFulfillmentModal.orderItems")}
                    <Badge variant="secondary" className="ml-1 text-xs">
                      {lineItems.length}
                    </Badge>
                  </h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {lineItems.map((item: any, idx: number) => (
                    <div
                      key={idx}
                      className="px-5 py-4 flex items-start justify-between hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Box className="h-5 w-5 text-slate-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                              {t("components.orders.orderFulfillmentModal.productId")}
                            </span>
                            <code className="text-sm font-semibold text-slate-900 bg-slate-100 px-2 py-0.5 rounded">
                              {item.productId}
                            </code>
                          </div>
                          {item.comments && (
                            <p className="text-sm text-slate-600 mt-1.5 break-words">
                              {item.comments}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        {item.price > 0 && item.quantity > 0 ? (
                          <div className="text-sm text-slate-700">
                            <span className="font-medium">{item.quantity}</span>
                            <span className="text-slate-400 mx-1">×</span>
                            <span className="font-medium">${formatCentsAsDollars(Math.round(item.price / item.quantity))}</span>
                            <span className="text-slate-400">/{formatUOMShort(item.uom)}</span>
                            <span className="text-slate-400 mx-1">=</span>
                            <span className="font-semibold text-primary">${formatCentsAsDollars(item.price)}</span>
                          </div>
                        ) : (
                          <div className="text-sm text-slate-700">
                            <span className="text-slate-500">{t("components.orders.orderFulfillmentModal.qty")}</span>
                            <span className="font-semibold ml-1">{item.quantity}</span>
                            {item.uom && item.uom.toLowerCase() !== 'ea' && (
                              <span className="text-slate-500 ml-1">{formatUOMShort(item.uom)}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Shipments Section */}
            {shipments.length > 0 ? (
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <Truck className="h-4 w-4 text-primary" />
                  {t("components.orders.orderFulfillmentModal.shipments")}
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {shipments.length}
                  </Badge>
                </h3>

                {shipments.map((shipment: any, idx: number) => (
                  <div
                    key={idx}
                    className="border border-slate-200 rounded-lg overflow-hidden bg-white"
                  >
                    {/* Shipment Header */}
                    <div className="bg-slate-50 px-5 py-3 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Truck className="h-4 w-4 text-slate-500" />
                          <span className="font-medium text-slate-900">
                            {t("components.orders.orderFulfillmentModal.shipment")}{shipment.id}
                          </span>
                          {shipment.shipVia && (
                            <span className="text-sm text-slate-500">via {shipment.shipVia}</span>
                          )}
                        </div>
                        {shipment.poNumber && (
                          <Badge variant="outline" className="text-xs font-mono">
                            {t("components.orders.orderFulfillmentModal.po")} {shipment.poNumber}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Shipment Details */}
                    <div className="p-5">
                      {/* Info Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                        {shipment.orderDate && (
                          <div className="bg-slate-50 rounded-md p-3">
                            <div className="flex items-center gap-1.5 text-slate-500 mb-1">
                              <Calendar className="h-3.5 w-3.5" />
                              <span className="text-xs font-medium">{t("components.orders.orderFulfillmentModal.orderDate")}</span>
                            </div>
                            <p className="text-sm font-semibold text-slate-900">
                              {formatDate(shipment.orderDate)}
                            </p>
                          </div>
                        )}

                        {shipment.requiredDate && (
                          <div className="bg-amber-50 rounded-md p-3 border border-amber-100">
                            <div className="flex items-center gap-1.5 text-amber-600 mb-1">
                              <Clock className="h-3.5 w-3.5" />
                              <span className="text-xs font-medium">{t("components.orders.orderFulfillmentModal.requiredBy")}</span>
                            </div>
                            <p className="text-sm font-semibold text-amber-900">
                              {formatDate(shipment.requiredDate)}
                            </p>
                          </div>
                        )}

                        {shipment.telephone && (
                          <div className="bg-slate-50 rounded-md p-3">
                            <div className="flex items-center gap-1.5 text-slate-500 mb-1">
                              <Phone className="h-3.5 w-3.5" />
                              <span className="text-xs font-medium">{t("components.orders.orderFulfillmentModal.contact")}</span>
                            </div>
                            <p className="text-sm font-semibold text-slate-900">{shipment.telephone}</p>
                          </div>
                        )}

                        {(() => {
                          const accountName = getHajocaShipmentAccountName(shipment);
                          const address = formatHajocaShipmentAddress(shipment);

                          if (!accountName && !address) return null;

                          return (
                            <div className="bg-blue-50 rounded-md p-3 border border-blue-100">
                              <div className="flex items-center gap-1.5 text-blue-600 mb-1">
                                <MapPin className="h-3.5 w-3.5" />
                                <span className="text-xs font-medium">{t("components.orders.orderFulfillmentModal.shipTo")}</span>
                              </div>
                              {accountName && (
                                <p className="text-sm font-semibold text-blue-900">{accountName}</p>
                              )}
                              {address && (
                                <p className="text-blue-700 text-xs mt-0.5 line-clamp-2">
                                  {address}
                                </p>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Shipping Instructions */}
                      {shipment.shippingInstructions && (
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-5">
                          <div className="flex items-start gap-2">
                            <FileText className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-medium text-amber-600 mb-0.5">
                                {t("components.orders.orderFulfillmentModal.shippingInstructions")}
                              </p>
                              <p className="text-sm text-amber-900 whitespace-pre-wrap">
                                {shipment.shippingInstructions}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Shipment Line Items */}
                      {shipment.lineItems && shipment.lineItems.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1.5">
                            <Package className="h-3.5 w-3.5" />
                            {t("components.orders.orderFulfillmentModal.itemsInThisShipment")}{shipment.lineItems.length})
                          </p>
                          <div className="bg-slate-50 rounded-md divide-y divide-slate-200">
                            {shipment.lineItems.map((shipmentItem: any, lineIdx: number) => {
                              // Look up the order line item to get product details
                              const orderItem = lineItemsMap.get(shipmentItem.orderlineitemId);
                              
                              return (
                                <div
                                  key={lineIdx}
                                  className="flex items-start justify-between p-3"
                                >
                                  <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                    <div className="w-7 h-7 bg-white rounded flex items-center justify-center flex-shrink-0 mt-0.5 border border-slate-200">
                                      <Box className="h-3.5 w-3.5 text-slate-400" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      {orderItem ? (
                                        <>
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <code className="text-xs font-semibold text-slate-900 bg-white px-1.5 py-0.5 rounded border border-slate-200">
                                              {orderItem.productId}
                                            </code>
                                          </div>
                                          {orderItem.comments && (
                                            <p className="text-xs text-slate-500 mt-1 break-words">
                                              {orderItem.comments}
                                            </p>
                                          )}
                                        </>
                                      ) : (
                                        <span className="text-sm text-slate-600">
                                          {t("components.orders.orderFulfillmentModal.item")}{shipmentItem.orderlineitemId}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0 ml-3 text-right">
                                    {shipmentItem.shipQuantity && shipmentItem.price ? (
                                      <div className="text-xs text-slate-700">
                                        {(() => {
                                          const qty = parseFloat(formatHajocaQuantity(shipmentItem.shipQuantity));
                                          const total = parseFloat(formatHajocaShipmentLineItemPrice(shipmentItem.price));
                                          const unitPrice = qty > 0 ? (total / qty).toFixed(2) : total.toFixed(2);
                                          const uom = formatUOMShort(orderItem?.uom);
                                          return (
                                            <>
                                              <span className="font-medium">{formatHajocaQuantity(shipmentItem.shipQuantity)}</span>
                                              <span className="text-slate-400 mx-1">×</span>
                                              <span className="font-medium">${unitPrice}</span>
                                              <span className="text-slate-400">/{uom}</span>
                                              <span className="text-slate-400 mx-1">=</span>
                                              <span className="font-semibold text-primary">${formatHajocaShipmentLineItemPrice(shipmentItem.price)}</span>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    ) : shipmentItem.shipQuantity ? (
                                      <span className="text-xs font-medium text-slate-700">
                                        {formatHajocaQuantity(shipmentItem.shipQuantity)} {formatUOMShort(orderItem?.uom)}
                                      </span>
                                    ) : shipmentItem.price ? (
                                      <span className="text-sm font-semibold text-primary">
                                        ${formatHajocaShipmentLineItemPrice(shipmentItem.price)}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Empty State - No Shipments */
              <div className="border border-dashed border-slate-300 rounded-lg p-8 text-center">
                <div className="bg-slate-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Truck className="h-7 w-7 text-slate-400" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-1">
                  {t("components.orders.orderFulfillmentModal.awaitingShipmentDetails")}
                </h3>
                <p className="text-sm text-slate-500 max-w-sm mx-auto">
                  {t("components.orders.orderFulfillmentModal.yourOrderIsBeing")}
                </p>
                <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-primary">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{t("components.orders.orderFulfillmentModal.checkBackSoonFor")}</span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-200 px-6 py-4 bg-slate-50">
            <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
              {t("common.close")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
